import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  createProjectGroup,
  deleteProjectGroup,
  getProjectGroups,
  updateProjectGroup,
} from '../projects/state/projects.actions';
import {
  createBucket,
  deleteBucket,
  bulkDeleteBuckets,
  getBuckets,
  updateBucket,
} from './state/buckets.actions';
import CardGrid from '../common/cardGrid/cardGrid';
import BreadcrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import BucketAddView from './BucketAddView';
import BucketCard from './BucketCard';
import { getIdMap, isNullOrUndefined, sortByLocalCompare } from '../helpers/helpers';
import {
  bucketLinkTypeToDisplayName,
  formatBucketPayload,
  getDownstreamName,
  getUpstreamName,
} from './bucketHelpers';
import Permissions from '../auth/Permissions';
import { getCustomers } from '../contacts/customers/state/customers.actions';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import { getEquipment } from '../equipment/state/equipment.actions';
import { getLabels } from '../labels/state/labels.actions';
import { getStatuses } from '../statuses/state/statuses.actions';
import TagRender from '../common/TagRender';

const getTypeMap = (t) => ({
  ProjectGroups: {
    name: t('ProjectGroup'),
    get: getProjectGroups,
    create: createProjectGroup,
    update: updateProjectGroup,
    delete: deleteProjectGroup,
    writePerm: 'PROJECTS_WRITE',
  },
  Buckets: {
    name: 'Bucket',
    get: getBuckets,
    create: createBucket,
    update: updateBucket,
    delete: deleteBucket,
    writePerm: 'BUCKETS_WRITE',
  },
});

const getProjectGroupCrumbs = (t) => [{
  text: t('ProjectGroup', { count: 2 }),
  icon: 'group',
}];

const getBucketSearchField = (item) => [
  item.name,
  item.description,
  item.number,
]
  .filter((value) => !isNullOrUndefined(value))
  .map(String)
  .join(' ');

export default function Bucket({
  type,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { bucketTemplateId } = useParams();
  const location = useLocation();

  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const buckets = useSelector((state) => state.buckets.buckets);
  const customers = useSelector((state) => state.customers.customers);
  const projectGroups = useSelector((state) => state.projects.projectGroups);
  const projects = useSelector((state) => state.projects.projects);
  const equipment = useSelector((state) => state.equipment.equipment);
  const statuses = useSelector((state) => state.statuses.statuses);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const useRedesign = settings?.uxRedesignPt1;

  const [crumbs, setCrumbs] = useState([]);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [activeSort, setActiveSort] = useState('Name: A to Z');
  const cardGridRef = useRef(null);

  useEffect(() => {
    const storedSort = window.localStorage.getItem('bucketsSortText');
    setActiveSort(storedSort || 'Name: A to Z');
  }, []);

  const typeMap = getTypeMap(t);
  const hasWritePerms = Permissions.has(typeMap[type].writePerm);

  // Extract targetId and activeTab from location state
  const { targetId, activeTab } = location?.state || {};

  useEffect(() => {
    if (type === 'Buckets') {
      dispatch(getCustomers());
      dispatch(getEquipment());
      dispatch(getStatuses());
    }
    dispatch(typeMap[type].get());
  }, [type]);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const bucketTemplateMap = useMemo(() => getIdMap(bucketTemplates), [bucketTemplates]);
  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);

  const relevantBucketTemplate = useMemo(() => (
    bucketTemplateMap[bucketTemplateId]
  ), [bucketTemplateMap, bucketTemplateId]);

  const relevantSource = useMemo(() => {
    const source = type === 'ProjectGroups'
      ? projectGroups
      : buckets.filter((bucket) => bucket.bucketTemplateId === bucketTemplateId)

    const sortedSource = [...source];
    sortedSource.sort(sortByLocalCompare('name', activeSort.includes('A to Z') ));

    return sortedSource;
  }, [projectGroups, buckets, bucketTemplateId, activeSort]);

  useEffect(() => {
    // Load crumbs
    if (type === 'ProjectGroups') {
      setCrumbs(getProjectGroupCrumbs(t));
      return;
    }

    setCrumbs([{
      text: relevantBucketTemplate?.name ?? 'Bucket',
      icon: relevantBucketTemplate?.icon ?? 'group',
    }]);
  }, [relevantBucketTemplate, type]);

  useEffect(() => {
    setCustomFields(relevantBucketTemplate?.customFields?.sections || []);
  }, [relevantBucketTemplate]);

  // Handle opening bucket/project group from location state
  const openItemFromLocationState = useCallback(() => {
    if (!targetId || !cardGridRef.current) {
      return;
    }

    const locationStateItem = relevantSource.find((item) => item.id === targetId);

    if (locationStateItem) {
      const timerName = type === 'ProjectGroups' ? 'openProjectGroupTimer' : 'openBucketTimer';
      clearTimeout(window[timerName]);
      window[timerName] = setTimeout(() => {
        cardGridRef.current.displayItem({
          ...locationStateItem,
          activeTab: activeTab || 'details',
        });
        window.history.replaceState({}, '', location.pathname);
      }, 100);
    }
  }, [targetId, cardGridRef, relevantSource, activeTab, type, location.pathname]);

  // Call openItemFromLocationState when targetId changes
  useEffect(() => {
    if (targetId && cardGridRef.current && relevantSource.length > 0) {
      const timer = setTimeout(() => {
        if (targetId && cardGridRef.current) {
          openItemFromLocationState();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [targetId, activeTab, openItemFromLocationState, location.state]);

  const validateAndFormatPayload = (payload) => {
    const formattedValues = formatCustomFieldCreatePayload({
      payload,
      customFields,
    });

    formattedValues.overrideLabel = formattedValues.overrideLabel?.map((label) => ({
      labelId: label.labelId || label.id,
      title: label.title,
    })) || null;

    if (formattedValues?.error) {
      setErrors(formattedValues.errorMap);
      return false;
    }

    setErrors({});
    return formattedValues;
  };

  const onAdd = async (bucket) => {
    const formattedPayload = validateAndFormatPayload(bucket);
    if (!formattedPayload) return false;

    const result = await dispatch(typeMap[type].create(
      formatBucketPayload(
        { ...formattedPayload, bucketTemplateId },
        relevantBucketTemplate?.upstreamTypes,
        relevantBucketTemplate?.downstreamTypes,
      ),
    ));

    if (formattedPayload.overrideLabel) dispatch(getLabels());
    return result;
  };

  const onEdit = async (id, bucket) => {
    const formattedPayload = validateAndFormatPayload(bucket);
    if (!formattedPayload) return false;

    const result = await dispatch(typeMap[type].update(
      id,
      formatBucketPayload(
        { ...formattedPayload, bucketTemplateId },
        relevantBucketTemplate?.upstreamTypes,
        relevantBucketTemplate?.downstreamTypes,
      ),
    ));

    if (formattedPayload.overrideLabel) dispatch(getLabels());
    return result;
  };

  const onDelete = ({ id, name }) => new Promise((resolve) => (
    CustomConfirmModal({
      title: `Delete ${typeMap[type].name} ${name}`,
      okText: 'Delete',
      content: `Are you sure you want to delete the ${typeMap[type].name.toLowerCase()} ${name}?`,
      onOk: async () => {
        const result = await dispatch(typeMap[type].delete(id));
        if (result) resolve(true);
      },
      onCancel() {
        resolve(true);
      },
    })
  ));

  const onBulkDelete = useCallback(async (ids, { password }) => (
    dispatch(bulkDeleteBuckets(ids, { password }))
  ), []);

  const sortData = useMemo(() => ({
    sortOptions: [
      {
        title: 'Name: A to Z',
        icon: 'sort-ascending',
        action: () => {
          window.localStorage.setItem('bucketsSortText', 'Name: A to Z');
          setActiveSort('Name: A to Z');
        },
      },
      {
        title: 'Name: Z to A',
        icon: 'sort-descending',
        action: () => {
          window.localStorage.setItem('bucketsSortText', 'Name: Z to A');
          setActiveSort('Name: Z to A');
        },
      },
    ],
    sortActive: activeSort,
  }), [activeSort]);

  const columnsToDisplay = useMemo(() => {
    const baseColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 100,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 200,
      },
    ];

    if (relevantBucketTemplate?.showAutoNumber) {
      baseColumns.push({
        title: 'Number',
        dataIndex: 'number',
        key: 'number',
        width: 80,
      });
    }

    if (type === 'Buckets') {
      baseColumns.push({
        title: 'Status',
        dataIndex: 'statusId',
        key: 'status',
        width: 100,
        render: (status) => {
          const relevantStatuses = statuses[relevantBucketTemplate?.id];
          return (
            <TagRender
              tagMap={relevantStatuses}
              tagIds={status ? [status] : []}
              useRedesign={useRedesign}
              labelKey="status"
            />
          );
        },
      });
    }

    if (type !== 'ProjectGroups' && relevantBucketTemplate?.upstreamTypes?.length) {
      relevantBucketTemplate.upstreamTypes.forEach((upstreamType) => {
        baseColumns.push({
          title: bucketLinkTypeToDisplayName(t, upstreamType, bucketTemplateMap),
          dataIndex: 'upstreamIds',
          key: 'upstreamIds',
          width: 300,
          render: (_, record) => (
            getUpstreamName({
              upstreamType,
              upstreamIds: record[upstreamType] ?? [],
              customers,
              t,
              bucketTemplateMap,
              bucketMap,
            })
          ),
        });
      });
    }

    if (type === 'ProjectGroups') {
      baseColumns.push({
        title: t('Project', { count: 0 }),
        dataIndex: 'downstreamIds',
        key: 'downstreamIds',
        width: 300,
        render: (_, record) => (
          getDownstreamName({
            ...record,
            downstreamIds: record.downstreamIds,
            entityType: type,
            projectMap,
            bucketMap,
            bucketTemplateMap,
            equipmentMap,
            t,
          })
        ),
      });
    }

    if (relevantBucketTemplate?.downstreamTypes?.length) {
      relevantBucketTemplate.downstreamTypes.forEach((downstreamType) => {
        baseColumns.push({
          title: bucketLinkTypeToDisplayName(t, downstreamType, bucketTemplateMap),
          dataIndex: 'downstreamIds',
          key: 'downstreamIds',
          width: 300,
          render: (_, record) => (
            getDownstreamName({
              downstreamIds: record[downstreamType] ?? [],
              downstreamType,
              projectMap,
              bucketMap,
              bucketTemplateMap,
              equipmentMap,
              t,
            })
          ),
        });
      });
    }

    return baseColumns;
  }, [
    projectMap,
    type,
    customers,
    projectMap,
    t,
    relevantBucketTemplate,
    bucketMap,
    bucketTemplateMap,
    equipmentMap,
    statuses,
    useRedesign,
  ]);

  const addView = (form) => (
    <BucketAddView
      {...form}
      hasWritePerms={hasWritePerms}
      type={type}
      bucketTemplate={relevantBucketTemplate}
      customFields={customFields}
      errors={errors}
    />
  );

  const editView = (form, formProps) => (
    <BucketAddView
      {...form}
      formProps={formProps}
      activeTab={formProps?.activeTab || activeTab || 'details'}
      hasWritePerms={hasWritePerms}
      type={type}
      bucketTemplate={relevantBucketTemplate}
      customFields={customFields}
      errors={errors}
    />
  );

  return (
    <BreadcrumbContainer crumbs={crumbs}>
      <CardGrid
        ref={cardGridRef}
        dataSource={relevantSource}
        search={{
          getField: getBucketSearchField,
        }}
        itemView={BucketCard}
        itemDimensions={{ width: 239, height: 190 }}
        isListView
        displayColumns={columnsToDisplay}
        sort={sortData}
        add={hasWritePerms ? {
          title: `Add ${typeMap[type].name}`,
          formView: addView,
          width: 1400,
          onClose: onAdd,
          zIndex: 890,
        } : null}
        edit={{
          title: `Edit ${typeMap[type].name}`,
          formView: editView,
          width: 1400,
          onClose: onEdit,
          getTitle: ({ name, editing }) => (editing ? `Edit ${name}` : name),
          canEdit: () => hasWritePerms,
          canArchive: () => false,
          canDelete: () => hasWritePerms,
          zIndex: 890,
        }}
        onDelete={onDelete}
        bulkActions={type === 'Buckets' && hasWritePerms ? {
          canArchive: false,
          canActivate: false,
          canDelete: true,
          deleteAllSelected: true,
          entityLabel: 'buckets',
          warnings: {
            delete: {
              subtext: 'This will remove the selected buckets and update related links.',
            },
          },
        } : null}
        onBulkDelete={type === 'Buckets' ? onBulkDelete : null}
        itemProps={{ entityType: type }}
        title={relevantBucketTemplate?.name ?? 'Buckets'}
      />
    </BreadcrumbContainer>
  );
}

Bucket.propTypes = {
  type: PropTypes.string.isRequired,
};
